import React, {useMemo, useEffect, useRef, ComponentRef} from 'react'
import styles from './UIGamePlayerInformationContainer.module.scss'
import {UIGamePlayerInformation} from './UIGamePlayerInformation'
import {PlayerColor, CardEnum, TradeResponseStatus, TurnState, GameState} from '@colonist/game-states'
import {
    CardValidatorOrdered,
    Point,
} from '@colonist/game-mechanic-validators'
import clsx from 'clsx'
import {useGameStore} from '../../store/GameStoreContext'
import {usePlayerValidators} from '../../store/selectors/usePlayerValidators'
import {UIGameDiceGroupContainer} from '../../common/UIGameDiceGroup/UIGameDiceGroupContainer'
import {useCurrentStateValidator} from '../../store/selectors/useCurrentStateValidator'
import {ModeSettingEnum} from '@colonist/types'
import {useMechanicValidatorKnight} from '../../store/selectors/useMechanicValidatorKnight'
import {useMechanicValidatorLargestArmy} from '../../store/selectors/useMechanicValidatorLargestArmy'
import {useMechanicValidatorLongestRoad} from '../../store/selectors/useMechanicValidatorLongestRoad'
import SimpleBar from 'simplebar-react'

interface UIGamePlayerInformationContainerProps {
    className?: string
}

type SimpleBarRef = ComponentRef<typeof SimpleBar>

export const getGlobalPositionOfPlayerCard = (playerColor: PlayerColor, isResourceCard: boolean): Point | undefined => {
    const playerElement = document.querySelector(`[data-player-color="${playerColor}"]`) as HTMLElement | null
    if(!playerElement) return undefined

    const uiGameContainer = document.getElementById('ui-game') as HTMLElement
    if(uiGameContainer == undefined) return undefined
    const uiGameRect = uiGameContainer.getBoundingClientRect()

    const dataTag = isResourceCard ? '[data-resource-card]' : '[data-development-card]'
    const cardElement = playerElement.querySelector(dataTag) as HTMLElement | null
    if(!cardElement) return undefined

    const cardElementRect = cardElement.getBoundingClientRect()
    return new Point(cardElementRect.left - uiGameRect.left, cardElementRect.top - uiGameRect.top)
}

export const getOpponentPlayerOrderWhereMyColorIsLast = (playOrder: PlayerColor[], myColor: PlayerColor): PlayerColor[] => {
    if(myColor === PlayerColor.None) return playOrder
    const newOrder: PlayerColor[] = []

    const myColorIndex = playOrder.indexOf(myColor)
    const numberOfPlayers = playOrder.length
    const indexOfPlayerAfterMe = myColorIndex + 1
    for(let index = indexOfPlayerAfterMe; index <= numberOfPlayers + myColorIndex; index++) {
        newOrder.push(playOrder[index % numberOfPlayers])
    }
    return newOrder.slice(0, -1)
}

const isPlayerTakingOutOfTurnAction = (playerColor: PlayerColor, gameState: GameState): boolean => {
    const currentPlayerState = gameState.playerStates[playerColor]
    if(currentPlayerState == undefined) return false
    if(currentPlayerState.isTakingAction) return true

    const isWaitingForOpponentTradeResponses = Object.keys(gameState.tradeState.activeOffers).length > 0 &&
        gameState.currentState.currentTurnPlayerColor === playerColor
    if(isWaitingForOpponentTradeResponses) return true

    for(const activeTradeState of Object.values(gameState.tradeState.activeOffers)) {
        if(activeTradeState == undefined) continue

        if(activeTradeState.playersCreatingCounterOffer[playerColor] === true) return true
        if(activeTradeState.playerResponses[playerColor] === TradeResponseStatus.PendingResponse) return true
    }

    return false
}

const scrollToCurrentTurnPlayer = (turnState: TurnState,
                                   currentTurnPlayerColor: PlayerColor,
                                   simplebarRef: SimpleBarRef,
                                   shouldAnimateScroll: boolean = true) => {
    const opponentsContainer = simplebarRef.contentWrapperEl
    if(opponentsContainer == undefined) return

    if(turnState !== TurnState.Dice) return

    const isScrollable = opponentsContainer.scrollHeight > opponentsContainer.clientHeight
    if(!isScrollable) return

    const currentTurnPlayerElement = opponentsContainer.querySelector(`[data-player-color="${currentTurnPlayerColor}"]`) as HTMLElement
    if(currentTurnPlayerElement == undefined) return

    const contentEl = simplebarRef.contentEl
    if(contentEl == undefined) return
    const playerIndex = Array.from(contentEl.children).indexOf(currentTurnPlayerElement)
    const itemHeight = currentTurnPlayerElement.clientHeight
    const gap = 0.2 * 16

    const scrollIndex = Math.max(0, playerIndex - 1)
    const scrollDestination = scrollIndex * (itemHeight + gap)

    if(shouldAnimateScroll) {
        opponentsContainer.scrollTo({
            top: scrollDestination,
            behavior: 'smooth',
        })
        return
    } else {
        opponentsContainer.scrollTop = scrollDestination
    }
}

export const UIGamePlayerInformationContainer = ({className}: UIGamePlayerInformationContainerProps) => {
    const gameState = useGameStore((state) => state.gameState)
    const currentStateValidator = useCurrentStateValidator()
    const isMyColorSpectator = useGameStore((state) => state.isMyColorSpectator)
    const myColor = useGameStore((state) => state.myColor)
    const modeSetting = useGameStore((state) => state.gameSettings.modeSetting)
    const victoryPointsToWin = useGameStore((state) => state.gameSettings.victoryPointsToWin)
    const userStates = useGameStore((state) => state.gameUserStates.userStates)
    const playOrder = useGameStore((state) => state.gameDetails.playOrder)
    const {getPlayerValidator} = usePlayerValidators()
    const translationKeyContext = useGameStore((state) => state.gameMode.translationKeyContext)
    const simplebarRef = useRef<SimpleBarRef | null>(null)

    useEffect(() => {
        const {turnState, currentTurnPlayerColor} = gameState.currentState
        const shouldScrollToCurrentTurnPlayer = turnState === TurnState.Dice && currentTurnPlayerColor !== myColor

        if(shouldScrollToCurrentTurnPlayer && simplebarRef.current) {
            scrollToCurrentTurnPlayer(turnState, currentTurnPlayerColor, simplebarRef.current, true)
        }
    }, [gameState.currentState.turnState, gameState.currentState.currentTurnPlayerColor, myColor])

    const effectiveMyColor = isMyColorSpectator ? playOrder[0] : myColor

    const longestRoadValidator = useMechanicValidatorLongestRoad()
    const largestArmyValidator = useMechanicValidatorLargestArmy()
    const knightValidator = useMechanicValidatorKnight()

    const merchantState = gameState.mechanicMerchantState

    const createPlayerInformationComponent = (playerColor: PlayerColor, containerClassName: string | undefined) => {
        const playerValidator = getPlayerValidator(playerColor)
        if(playerValidator == undefined) return undefined

        const isCurrentUser = playerColor === effectiveMyColor

        const resourceCards = new CardValidatorOrdered(playerValidator.resourceCards)
        const developmentCardsState = gameState.mechanicDevelopmentCardsState?.players[playerColor]
        const developmentCards = new CardValidatorOrdered(developmentCardsState?.developmentCards ?? {cards: []})
        const progressCardsState = gameState.mechanicProgressCardsState?.players[playerColor]
        const progressCards = new CardValidatorOrdered(progressCardsState?.progressCards ?? {cards: []})
        const cityImprovementState = gameState.mechanicCityImprovementsState?.[playerColor] ?? {}

        const longestRoadPlayerState = longestRoadValidator?.getLongestRoadPlayerState(playerColor)
        const longestRoadCount = longestRoadPlayerState?.longestRoad ?? 0
        const hasLongestRoad = longestRoadValidator?.getPlayerWithLongestRoadCard() === playerColor

        const hasLargestArmy = largestArmyValidator?.getPlayerWithLargestArmy() === playerColor

        const knightPlayerValidator = knightValidator?.getKnightPlayerValidator(playerColor)
        const knightStrength = knightPlayerValidator?.getPlayerKnightStrength() ?? 0
        const doesEveryPlayerHaveZeroKnightStrength = knightValidator?.getTotalKnightStrength() === 0
        const hasHighestKnightStrength = doesEveryPlayerHaveZeroKnightStrength ? false :
            knightValidator?.getPlayersWithHighestKnightStrength().includes(playerColor) ?? false

        const developmentCardsUsed = developmentCardsState?.developmentCardsUsed ?? []
        const knightDevelopmentCardsUsedCount = developmentCardsUsed.filter((card) => card === CardEnum.Knight).length

        const hasMerchant = merchantState?.owner === playerColor
        const hasRobber = modeSetting === ModeSettingEnum.ColonistRush4P
        && gameState.currentState.turnState !== TurnState.InitialPlacement
        && gameState.currentState.currentTurnPlayerColor === playerColor

        const isCurrentPlayerTurn = currentStateValidator.currentActivePlayers.includes(playerColor)
        const hasSpecialBuildPhaseFlag = gameState.currentState.turnState === TurnState.SpecialBuildPhase &&
            isCurrentPlayerTurn

        const isTakingAction = isPlayerTakingOutOfTurnAction(playerColor, gameState) && playerColor !== myColor

        const diceComponent = !isCurrentUser && modeSetting !== ModeSettingEnum.ColonistRush4P ? (
            <UIGameDiceGroupContainer
                className={styles.diceGroup}
                playerColor={playerColor}
                size='medium'
                disableDiceSound
            />
        ) : null
        const playerInfo = (
            <UIGamePlayerInformation
                className={styles.playerInformation}
                amountOfResourceCards={resourceCards.getNumberOfCardsInInventory()}
                cardDiscardLimit={playerValidator.cardDiscardLimit}
                cityImprovementState={cityImprovementState}
                developmentCards={developmentCards}
                gameMode={modeSetting}
                hasHighestKnightStrength={hasHighestKnightStrength}
                hasLargestArmy={hasLargestArmy}
                hasLongestRoad={hasLongestRoad}
                hasMerchant={hasMerchant}
                hasSpecialBuildPhaseFlag={hasSpecialBuildPhaseFlag}
                isActive={isCurrentPlayerTurn}
                isCurrentUser={isCurrentUser}
                isTakingAction={isTakingAction}
                knightDevelopmentCardsUsedCount={knightDevelopmentCardsUsedCount}
                knightStrength={knightStrength}
                longestRoadCount={longestRoadCount}
                playerValidator={playerValidator}
                progressCards={progressCards}
                translationContext={translationKeyContext}
                victoryPointsToWin={victoryPointsToWin}
                hasRobber={hasRobber}
            />
        )

        return (
            <div
                className={containerClassName}
                data-player-color={playerColor}
                key={playerColor}>
                {diceComponent}
                {playerInfo}
            </div>
        )
    }

    const opponentPlayerComponents = useMemo(() => {
        const playerExceptMyColor = getOpponentPlayerOrderWhereMyColorIsLast(playOrder, effectiveMyColor)
        const opponentComponents = playerExceptMyColor.map((playerColor) =>
            createPlayerInformationComponent(playerColor, clsx(styles.opponentPlayerRow, styles.playerRow)))

        return opponentComponents
    }, [gameState, userStates, myColor, modeSetting, victoryPointsToWin, playOrder, isMyColorSpectator, currentStateValidator])

    const mainUserComponent = createPlayerInformationComponent(effectiveMyColor, styles.playerRow)

    return (
        <div className={clsx(styles.container, className)} data-player-information-container>
            <SimpleBar
                ref={simplebarRef}
                className={styles.opponentsScrollContainer}
                classNames={{
                    contentEl: styles.opponentsScrollContainerScrollContent,
                }}
            >
                {opponentPlayerComponents}
            </SimpleBar>
            {mainUserComponent}
        </div>
    )
}
